import React from "react";
import {
  stripUsernameDomain,
  getEffectiveChangedBy,
} from "../utility/computeScheduleDiff";

/**
 * Time Machine #114 Phase 6 — stacked username bubbles for the Diff Pair view.
 *
 * Renders one small red bubble per change event from the (T1, T2] window,
 * stacked oldest-to-newest top-to-bottom. The bubble text is the event's
 * changedBy username with @domain stripped (alice@kellerinteriors.com →
 * "alice"). All employees share the same domain so this turns email
 * strings into name-like labels.
 *
 * If the event count exceeds maxVisible (default 4), the bottom-most
 * bubble becomes "+N more" — a hint that the full chronological detail
 * lives in the per-job/crew History modal (Phase 5). Click-through is
 * NOT wired in this component; clicks bubble up to the parent JobTile or
 * ScheduleCell whose own click handler opens that modal.
 *
 * Props:
 *   events     — array of EventLog entries (must have .changedBy)
 *   maxVisible — max bubbles before the "+N more" fold-up
 *   muted      — if true, render at lower opacity (banner mute checkbox, iter 6)
 *   anchorClassName — Tailwind classes for the absolute-positioned wrapper.
 *                     Defaults to inside-the-tile top-right (top-0.5 right-0.5).
 *                     ScheduleCell has overflow-hidden so external anchors
 *                     (e.g. -top-2 -right-2) get clipped at the cell edge —
 *                     visible bug from Phase 6 dev validation. Anchoring
 *                     INSIDE the tile keeps the bubble fully visible.
 *
 * Pure / no side effects.
 */
const UserBubbleStack = ({
  events = [],
  maxVisible = 4,
  muted = false,
  anchorClassName = "top-0.5 right-0.5",
}) => {
  if (!Array.isArray(events) || events.length === 0) return null;

  const overflowCount = Math.max(0, events.length - maxVisible);
  const visibleEvents = overflowCount > 0 ? events.slice(0, maxVisible - 1) : events;
  const opacity = muted ? 0.35 : 1;

  return (
    <div
      data-tutorial-target="user-bubble"
      className={`absolute ${anchorClassName} flex flex-col gap-0.5 z-20 pointer-events-none`}
      style={{ opacity }}
    >
      {visibleEvents.map((ev, i) => {
        // Phase 6 user-attribution fallback: when changedBy is "unknown"
        // (FileMaker-driven write — no Scheduler-UI user to log), fall back
        // to the doc's _modificationAccount / _creationAccount which FileMaker
        // stamps directly on the record. See computeScheduleDiff.js
        // getEffectiveChangedBy for the full resolution order.
        const effective = getEffectiveChangedBy(ev);
        const display = stripUsernameDomain(effective) || "unknown";
        // Tooltip shows the raw changedBy from the Lambda + the effective
        // attribution if they differ — preserves both the literal Lambda
        // value AND the FM-fallback name so support staff can tell which.
        const rawChangedBy = ev?.changedBy ?? "unknown";
        const tooltipUser =
          effective !== rawChangedBy
            ? `${effective} (FileMaker; Lambda recorded "${rawChangedBy}")`
            : rawChangedBy;
        return (
          <span
            key={ev?._id ?? i}
            // `pointer-events-auto` is REQUIRED on each bubble so the
            // browser's native `title` tooltip fires on hover. The
            // wrapper div above sets `pointer-events-none` so clicks on
            // EMPTY space between bubbles still pass through to the
            // underlying tile (which opens the History modal). Hover and
            // click bubble up through the DOM independently, so re-
            // enabling pointer events on the bubbles themselves does not
            // break click-through: a click on the bubble registers on
            // the span, has no onClick of its own, and bubbles up to the
            // tile's onClick handler.
            className="text-[0.625rem] leading-tight font-semibold text-white px-1.5 py-0.5 rounded-full whitespace-nowrap shadow-xs pointer-events-auto cursor-help"
            style={{
              backgroundColor: "#dc2626", // red-600 — not in the custom palette
              border: "1px solid #ffffff",
            }}
            title={`${tooltipUser} • ${ev?.timestamp ?? ""}`}
          >
            {display}
          </span>
        );
      })}
      {overflowCount > 0 && (
        <span
          className="text-[0.625rem] leading-tight font-semibold text-white px-1.5 py-0.5 rounded-full whitespace-nowrap shadow-xs pointer-events-auto cursor-help"
          style={{
            backgroundColor: "#7f1d1d", // red-900 — darker variant for the overflow chip
            border: "1px solid #ffffff",
          }}
          title="See full History feed in this job/crew's modal"
        >
          +{overflowCount + 1} more
        </span>
      )}
    </div>
  );
};

export default UserBubbleStack;
